import { captureMessage } from '@/lib/sentry';

interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

interface NotificationError extends Error {
  code?: string;
  statusCode?: number;
  retryable?: boolean;
  context?: Record<string, any>;
}

class NotificationErrorHandler {
  private static instance: NotificationErrorHandler;
  private retryConfig: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    backoffMultiplier: 2,
  };

  private constructor() {}

  static getInstance(): NotificationErrorHandler {
    if (!NotificationErrorHandler.instance) {
      NotificationErrorHandler.instance = new NotificationErrorHandler();
    }
    return NotificationErrorHandler.instance;
  }

  /**
   * Execute operation with retry logic and error handling
   */
  async executeWithRetry<T>(
    operation: () => Promise<T> | T,
    context: string,
    customConfig?: Partial<RetryConfig>
  ): Promise<T> {
    const config = { ...this.retryConfig, ...customConfig };
    let lastError: NotificationError | undefined;

    // Treat maxRetries as the total number of attempts
    for (let attempt = 0; attempt < config.maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        // Normalize the caught error before using it (was previously missing,
        // leaving lastError undefined and throwing in logError on error.message)
        lastError = this.normalizeError(error, context);

        // Log the error with context (1-based attempt for readability)
        this.logError(lastError, attempt, context);

        const isLastAttempt = attempt === config.maxRetries - 1;

        // Don't retry if it's the last attempt or error is not retryable
        if (isLastAttempt || !this.isRetryable(lastError)) {
          break;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          config.baseDelay * Math.pow(config.backoffMultiplier, attempt),
          config.maxDelay
        );

        await this.delay(delay);
      }
    }

    // If we get here, all retries failed
    throw this.createFinalError(lastError!, context, config);
  }

  /**
   * Normalize different error types into a consistent format
   */
  private normalizeError(error: any, context: string): NotificationError {
    if (error instanceof Error) {
      const notificationError = error as NotificationError;
      notificationError.context = { ...notificationError.context, operation: context };

      // Default to retryable for generic errors unless explicitly marked otherwise
      if (notificationError.retryable === undefined) {
        notificationError.retryable = true;
      }

      return notificationError;
    }

    // Handle API errors
    if (error?.response) {
      const apiError = new Error(
        error.response.data?.message || 'API request failed'
      ) as NotificationError;
      apiError.statusCode = error.response.status;
      apiError.code = error.response.data?.code || 'API_ERROR';
      apiError.retryable = this.isRetryableStatusCode(error.response.status);
      apiError.context = { operation: context, response: error.response.data };
      return apiError;
    }

    // Handle network errors
    if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network')) {
      const networkError = new Error('Network connection failed') as NotificationError;
      networkError.code = 'NETWORK_ERROR';
      networkError.retryable = true;
      networkError.context = { operation: context };
      return networkError;
    }

    // Generic error
    const genericError = new Error(
      typeof error === 'string' ? error : 'Unknown error occurred'
    ) as NotificationError;
    genericError.code = 'UNKNOWN_ERROR';
    // Treat any errors as retryable by default so transient issues get multiple attempts
    genericError.retryable = true;
    genericError.context = { operation: context, originalError: error };
    return genericError;
  }

  /**
   * Determine if an error is retryable
   */
  private isRetryable(error: NotificationError): boolean {
    if (error.retryable !== undefined) {
      return error.retryable;
    }

    // Network errors are retryable
    if (error.code === 'NETWORK_ERROR') {
      return true;
    }

    // Server errors (5xx) are retryable
    if (error.statusCode && error.statusCode >= 500) {
      return true;
    }

    // Rate limiting is retryable
    if (error.statusCode === 429) {
      return true;
    }

    // Client errors (4xx) are generally not retryable
    if (error.statusCode && error.statusCode >= 400 && error.statusCode < 500) {
      return false;
    }

    return false;
  }

  /**
   * Check if HTTP status code is retryable
   */
  private isRetryableStatusCode(statusCode: number): boolean {
    return statusCode >= 500 || statusCode === 429;
  }

  /**
   * Log error with appropriate level and context
   */
  private logError(error: NotificationError, attempt: number, context: string): void {
    const logData = {
      error: error.message,
      code: error.code,
      statusCode: error.statusCode,
      attempt: attempt + 1,
      context,
      retryable: error.retryable,
      timestamp: new Date().toISOString(),
      ...error.context,
    };

    if (attempt === 0) {
      console.warn('Notification operation failed, retrying...', logData);
    } else {
      console.error('Notification retry failed', logData);
    }

    // In production, send to monitoring service
    if (import.meta.env.PROD) {
      this.sendToMonitoring(logData);
    }
  }

  /**
   * Create final error after all retries failed
   */
  private createFinalError(
    lastError: NotificationError,
    context: string,
    config?: RetryConfig
  ): NotificationError {
    const maxRetriesUsed = config?.maxRetries ?? this.retryConfig.maxRetries;

    const finalError = new Error(
      `Notification operation failed after ${maxRetriesUsed} retries: ${lastError.message}`
    ) as NotificationError;

    finalError.code = 'MAX_RETRIES_EXCEEDED';
    finalError.statusCode = lastError.statusCode;
    finalError.retryable = false;
    finalError.context = {
      ...lastError.context,
      operation: context,
      maxRetries: maxRetriesUsed,
    };

    return finalError;
  }

  /**
   * Delay execution for specified milliseconds
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Send error data to monitoring service
   */
  private sendToMonitoring(errorData: Record<string, any>): void {
    captureMessage(
      `Notification failure: ${errorData.context || 'unknown'}`,
      errorData.attempt === 1 ? 'warning' : 'error',
      {
        ...errorData,
        error_type: 'notification_failure',
      }
    );
  }

  /**
   * Create user-friendly error messages
   */
  getUserFriendlyMessage(error: NotificationError): string {
    switch (error.code) {
      case 'NETWORK_ERROR':
        return 'Unable to connect to the server. Please check your internet connection.';
      case 'TIMEOUT_ERROR':
        return 'The request took too long. Please try again.';
      case 'AUTH_ERROR':
        return 'Authentication failed. Please sign in again.';
      case 'API_ERROR':
        if (error.statusCode === 401) {
          return 'Please refresh the page to continue.';
        }
        if (error.statusCode === 403) {
          return 'You do not have permission to perform this action.';
        }
        if (error.statusCode === 429) {
          return 'Too many requests. Please wait a moment and try again.';
        }
        return 'Server error occurred. Please try again later.';
      case 'MAX_RETRIES_EXCEEDED':
        return 'Operation failed after multiple attempts. Please try again later.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }
}

export const notificationErrorHandler = NotificationErrorHandler.getInstance();
export type { NotificationError };
